/* eslint-disable */
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga';
import '../b2bPage/b2bPage.css';
import '../b2bPage/b2bGammaTheme.module.css';
import './StartManyPage.css';
import { Button } from '@ui';
import { isStationAllowReservation } from '../../utils/utils';
import ErrorPage from '../errorPage/ErrorPage';
import StartManyLocationHeader from './StartManyLocationHeader';
import StartPageStationNotes from '../startPage/StartPageStationNotes';
import { StationMediaCarousel } from '../../components/stationMediaCarousel/StationMediaCarousel';
import Modal from '../../components/modal/Modal';
import { useGetClientId, isNativeShellApp } from '../../hookServices/clientId';
import { api, useGetBalanceQuery } from '../../redux/api';
import { paidPortsInHistory } from '../startPage/paidSession';
import { buildGoogleMapsNavigationUrl, buildWazeNavigationUrl } from '../homePage/mapPortSearchUtils';
import PortSummaryCard from '../../components/PortSummaryCard/PortSummaryCard';
import ServiceRequestModal from '../startPage/ServiceRequestModal';
import LeaveFeedbackModal from '../startPage/LeaveFeedbackModal';
import { StationPublicReportsSection } from '../startPage/StationPublicReports';
import OutageScheduleButton from '../../components/outageSchedule/OutageScheduleButton';
import '../../components/buttons/mainBtn.css';

const PortStatusCard = memo(function PortStatusCard({ station, onPortClick, portCount = 1 }) {
  const { t } = useTranslation();

  return (
    <PortSummaryCard
      station={station}
      portCount={portCount}
      onClick={() => onPortClick(station.portNumber)}
      ariaLabel={t('goToPort')}
    />
  );
});

export default memo(function StartManyPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { stations, isErrorStations } = useOutletContext();
  const { clientId } = useGetClientId();
  const isNativeApp = isNativeShellApp();
  const { data: freshBalance, isSuccess: isBalanceLoaded } = useGetBalanceQuery(clientId, {
    skip: !clientId || isNativeApp,
  });
  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
  } = api.useGetHistoryDataQuery(clientId, { skip: !clientId });
  const isHistoryReady = !!clientId && !isHistoryLoading && (historyData !== undefined || isHistoryError);
  const [bookingInstallOpen, setBookingInstallOpen] = useState(false);
  const [serviceRequestModalOpen, setServiceRequestModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [reportsReload, setReportsReload] = useState(0);
  const bumpReports = useCallback(() => setReportsReload(value => value + 1), []);

  useEffect(() => {
    document.body.classList.add('b2b-route');
    const layout = document.getElementById('layout');
    layout?.classList.add('b2b-route-layout');
    return () => {
      document.body.classList.remove('b2b-route');
      layout?.classList.remove('b2b-route-layout');
    };
  }, []);

  const handlePortClick = useMemo(
    () => portNumber => {
      navigate(`/start?station=${portNumber}`);
    },
    [navigate]
  );

  const validStations = useMemo(() => (stations || []).filter(s => !s.isError), [stations]);

  const mapsAnchor = useMemo(() => {
    if (!validStations.length) {
      return null;
    }
    return validStations.find(s => s.latitude != null && s.longitude != null) || validStations[0];
  }, [validStations]);

  const googleMapsUrl = useMemo(
    () => (mapsAnchor ? buildGoogleMapsNavigationUrl(mapsAnchor.latitude, mapsAnchor.longitude) : null),
    [mapsAnchor]
  );
  const wazeMapsUrl = useMemo(
    () => (mapsAnchor ? buildWazeNavigationUrl(mapsAnchor.latitude, mapsAnchor.longitude) : null),
    [mapsAnchor]
  );

  const bookingEnabled = useMemo(
    () => validStations.some(station => isStationAllowReservation(station)),
    [validStations]
  );

  const shouldShowBookingInstallPopup = !isNativeApp && bookingEnabled && isBalanceLoaded && Number(freshBalance) === 0;

  useEffect(() => {
    if (shouldShowBookingInstallPopup) {
      setBookingInstallOpen(true);
    }
  }, [shouldShowBookingInstallPopup]);

  const installStationNumber = validStations[0]?.portNumber ?? validStations[0]?.number ?? '';

  const handleInstallAppClick = () => {
    ReactGA.event({
      category: 'installApp',
      action: 'startMany booking install 220-km app popup',
    });
    setBookingInstallOpen(false);
    navigate(`/install-app?station=${encodeURIComponent(installStationNumber)}`);
  };

  const portCountByKey = useMemo(() => {
    const counts = new Map();
    for (const station of validStations) {
      const deviceKey = station.deviceId != null ? String(station.deviceId).trim() : '';
      const key = deviceKey || `_port_${station.portNumber ?? station.number ?? ''}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return counts;
  }, [validStations]);

  const getStationPortCount = station => {
    const deviceKey = station?.deviceId != null ? String(station.deviceId).trim() : '';
    const key = deviceKey || `_port_${station?.portNumber ?? station?.number ?? ''}`;
    return portCountByKey.get(key) || 1;
  };

  const reportStationNumbers = useMemo(
    () =>
      validStations
        .map(station => station.portNumber ?? station.number)
        .filter(value => value != null && String(value).trim() !== '')
        .map(value => String(value)),
    [validStations]
  );
  const paidFeedbackPorts = useMemo(
    () => paidPortsInHistory(historyData, reportStationNumbers),
    [historyData, reportStationNumbers]
  );
  const canLeaveFeedback = isHistoryError || !isHistoryReady || paidFeedbackPorts.length > 0;
  const feedbackStationNumber = paidFeedbackPorts[0] || reportStationNumbers[0] || '';

  const notesStation = useMemo(() => {
    if (!validStations.length) {
      return null;
    }
    return [...validStations].sort(
      (a, b) => Number(a.portNumber ?? a.number ?? 0) - Number(b.portNumber ?? b.number ?? 0)
    )[0];
  }, [validStations]);

  const mediaStations = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const station of validStations) {
      const deviceKey = station.deviceId != null ? String(station.deviceId).trim() : '';
      const webcamKey = station?.webcamUrl ? String(station.webcamUrl).trim() : '';
      const dedupeKey = deviceKey || webcamKey || String(station.portNumber ?? station.number ?? '').trim();
      if (!dedupeKey || seen.has(dedupeKey)) {
        continue;
      }
      seen.add(dedupeKey);
      list.push({
        stationNumber: station.portNumber ?? station.number,
        protocolType: station.protocolType,
        webcamUrl: station.webcamUrl,
      });
    }
    return list;
  }, [validStations]);

  if (isErrorStations) {
    return <ErrorPage errorHeader={t('errorDevHeader')} errorBody={t('errorDevBody')} />;
  }

  if (!stations || stations.length === 0) {
    return <ErrorPage errorHeader={t('noPortsFound')} errorBody={t('noPortsSpecified')} />;
  }

  return (
    <div id="startManyPage" className="start-many-page">
      <StartManyLocationHeader stations={validStations} />
      <div className="start-many-container start-many-container--list">
        <div className="start-many-port-list">
          {validStations.map((station, index) => (
            <PortStatusCard
              key={station.portNumber || index}
              station={station}
              onPortClick={handlePortClick}
              portCount={getStationPortCount(station)}
            />
          ))}
        </div>
        {mediaStations.length > 0 && (
          <div className="start-many-toka-photos">
            {mediaStations.map(({ stationNumber, protocolType, webcamUrl }) => (
              <StationMediaCarousel
                key={stationNumber}
                stationNumber={stationNumber}
                protocolType={protocolType}
                webcamUrl={webcamUrl}
              />
            ))}
          </div>
        )}
        {notesStation ? (
          <div className="start-many-station-notes">
            <StartPageStationNotes station={notesStation} />
          </div>
        ) : null}
        <div className="start-many-footer-actions start-page-footer-actions">
          <Button
            variant="outline"
            className="start-many-service-request-btn start-many-navigate-btn"
            disabled={!wazeMapsUrl}
            onClick={() => wazeMapsUrl && window.open(wazeMapsUrl, '_blank', 'noopener,noreferrer')}
          >
            <span className="start-many-service-request-btn__label">{t('openMapsInWaze')}</span>
          </Button>
          <Button
            variant="outline"
            className="start-many-service-request-btn start-many-navigate-btn"
            disabled={!googleMapsUrl}
            onClick={() => googleMapsUrl && window.open(googleMapsUrl, '_blank', 'noopener,noreferrer')}
          >
            <span className="start-many-service-request-btn__label">{t('openMapsInGoogle')}</span>
          </Button>
          <OutageScheduleButton
            latitude={mapsAnchor?.latitude}
            longitude={mapsAnchor?.longitude}
            stationNumber={mapsAnchor?.portNumber ?? mapsAnchor?.number}
            className="start-many-service-request-btn start-many-outage-btn"
            labelClassName="start-many-service-request-btn__label"
            labelStyle={null}
          />
        </div>
        <StationPublicReportsSection
          stationNumbers={reportStationNumbers}
          reloadToken={reportsReload}
          onFeedback={() => {
            ReactGA.event({
              category: 'feedback',
              action: 'open leave feedback',
              label: String((mapsAnchor || validStations[0])?.portNumber || ''),
            });
            setFeedbackModalOpen(true);
          }}
          onService={() => setServiceRequestModalOpen(true)}
        />
        <LeaveFeedbackModal
          modalOpen={feedbackModalOpen}
          setModalOpen={setFeedbackModalOpen}
          station={mapsAnchor || validStations[0]}
          stationNumber={feedbackStationNumber}
          clientId={clientId}
          canLeaveFeedback={canLeaveFeedback}
          onSubmitted={bumpReports}
        />
        <ServiceRequestModal
          modalOpen={serviceRequestModalOpen}
          setModalOpen={setServiceRequestModalOpen}
          station={mapsAnchor || validStations[0]}
          stationNumber={(mapsAnchor || validStations[0])?.portNumber}
          clientId={clientId}
          onSubmitted={bumpReports}
        />
      </div>
      {bookingInstallOpen && (
        <Modal setModalOpen={setBookingInstallOpen} modalOpen={bookingInstallOpen}>
          <div style={{ position: 'relative', textAlign: 'center', padding: '8px 4px 4px' }}>
            <button
              type="button"
              aria-label={t('close')}
              onClick={() => setBookingInstallOpen(false)}
              style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                width: '48px',
                height: '48px',
                border: 'none',
                background: 'transparent',
                color: '#fff',
                fontSize: '40px',
                fontWeight: 300,
                lineHeight: 1,
                cursor: 'pointer',
                padding: 0,
                zIndex: 1,
              }}
            >
              ×
            </button>
            <p
              style={{
                fontSize: '18px',
                margin: '12px 0 24px',
                color: 'rgba(255, 255, 255, 0.85)',
                lineHeight: '1.5',
                padding: '0 40px',
              }}
            >
              {t('reservation.webInstallAppPopup')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', width: '100%' }}>
              <Button
                onClick={handleInstallAppClick}
                style={{
                  borderRadius: '20px',
                  minHeight: '56px',
                  display: 'flex',
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                }}
              >
                <span className="mainBtnText">{t('installApp')}</span>
              </Button>
              <Button
                variant="outline-alt"
                onClick={() => setBookingInstallOpen(false)}
                style={{
                  borderRadius: '20px',
                  minHeight: '56px',
                  display: 'flex',
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                }}
              >
                <span style={{ fontSize: '22px', letterSpacing: '1px' }}>{t('close')}</span>
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
});
